/**
 * Validation schema builder similar to Yup
 * Provides a fluent API for creating validation rules
 */
class ValidationSchema {
  constructor() {
    this.rules = {};
  }

  /**
   * Create a new validation schema
   * @returns {ValidationSchema} New validation schema instance
   */
  static object() {
    return new ValidationSchema();
  }

  /**
   * Add a field to the schema
   * @param {string} fieldName - Field name
   * @param {Object} rules - Validation rules
   * @returns {ValidationSchema} Current schema instance
   */
  field(fieldName, rules = {}) {
    this.rules[fieldName] = rules;
    return this;
  }

  /**
   * Add required validation
   * @param {string} fieldName - Field name
   * @param {string|boolean} message - Error message or true for default
   * @returns {ValidationSchema} Current schema instance
   */
  required(fieldName, message = true) {
    if (!this.rules[fieldName]) {
      this.rules[fieldName] = {};
    }
    this.rules[fieldName].required = message;
    return this;
  }

  /**
   * Add type validation
   * @param {string} fieldName - Field name
   * @param {string} type - Type to validate (email, phone, number, url, date)
   * @returns {ValidationSchema} Current schema instance
   */
  type(fieldName, type) {
    if (!this.rules[fieldName]) {
      this.rules[fieldName] = {};
    }
    this.rules[fieldName].type = type;
    return this;
  }

  /**
   * Add min length validation
   * @param {string} fieldName - Field name
   * @param {number} minLength - Minimum length
   * @param {string} message - Error message
   * @returns {ValidationSchema} Current schema instance
   */
  minLength(fieldName, minLength, message) {
    if (!this.rules[fieldName]) {
      this.rules[fieldName] = {};
    }
    this.rules[fieldName].minLength = minLength;
    if (message) {
      this.rules[fieldName].minLengthMessage = message;
    }
    return this;
  }

  /**
   * Add max length validation
   * @param {string} fieldName - Field name
   * @param {number} maxLength - Maximum length
   * @param {string} message - Error message
   * @returns {ValidationSchema} Current schema instance
   */
  maxLength(fieldName, maxLength, message) {
    if (!this.rules[fieldName]) {
      this.rules[fieldName] = {};
    }
    this.rules[fieldName].maxLength = maxLength;
    if (message) {
      this.rules[fieldName].maxLengthMessage = message;
    }
    return this;
  }

  /**
   * Add min value validation
   * @param {string} fieldName - Field name
   * @param {number} min - Minimum value
   * @param {string} message - Error message
   * @returns {ValidationSchema} Current schema instance
   */
  min(fieldName, min, message) {
    if (!this.rules[fieldName]) {
      this.rules[fieldName] = {};
    }
    this.rules[fieldName].min = min;
    if (message) {
      this.rules[fieldName].minMessage = message;
    }
    return this;
  }

  /**
   * Add max value validation
   * @param {string} fieldName - Field name
   * @param {number} max - Maximum value
   * @param {string} message - Error message
   * @returns {ValidationSchema} Current schema instance
   */
  max(fieldName, max, message) {
    if (!this.rules[fieldName]) {
      this.rules[fieldName] = {};
    }
    this.rules[fieldName].max = max;
    if (message) {
      this.rules[fieldName].maxMessage = message;
    }
    return this;
  }

  /**
   * Add pattern validation
   * @param {string} fieldName - Field name
   * @param {string} pattern - Regex pattern
   * @param {string} message - Error message
   * @returns {ValidationSchema} Current schema instance
   */
  pattern(fieldName, pattern, message) {
    if (!this.rules[fieldName]) {
      this.rules[fieldName] = {};
    }
    this.rules[fieldName].pattern = pattern;
    if (message) {
      this.rules[fieldName].patternMessage = message;
    }
    return this;
  }

  /**
   * Add custom validation
   * @param {string} fieldName - Field name
   * @param {Function} validator - Custom validation function
   * @returns {ValidationSchema} Current schema instance
   */
  validate(fieldName, validator) {
    if (!this.rules[fieldName]) {
      this.rules[fieldName] = {};
    }
    this.rules[fieldName].validate = validator;
    return this;
  }

  /**
   * Get the validation rules object
   * @returns {Object} Validation rules object
   */
  toObject() {
    return this.rules;
  }
}

/**
 * Create a validation schema
 * @returns {ValidationSchema} Validation schema instance
 */
const createValidationSchema = () => ValidationSchema.object();

/**
 * Predefined validation schemas
 */
const validationSchemas = {
  // Email validation
  email: (fieldName, required = true) => {
    const schema = createValidationSchema();
    if (required) {
      schema.required(fieldName, 'Email is required');
    }
    return schema.type(fieldName, 'email');
  },

  // Phone validation
  phone: (fieldName, required = true) => {
    const schema = createValidationSchema();
    if (required) {
      schema.required(fieldName, 'Phone number is required');
    }
    return schema.type(fieldName, 'phone');
  },

  // Number validation
  number: (fieldName, required = true, min = null, max = null) => {
    const schema = createValidationSchema();
    if (required) {
      schema.required(fieldName, 'Number is required');
    }
    schema.type(fieldName, 'number');
    if (min !== null) {
      schema.min(fieldName, min);
    }
    if (max !== null) {
      schema.max(fieldName, max);
    }
    return schema;
  },

  // Text validation
  text: (fieldName, required = true, minLength = null, maxLength = null) => {
    const schema = createValidationSchema();
    if (required) {
      schema.required(fieldName, 'This field is required');
    }
    if (minLength !== null) {
      schema.minLength(fieldName, minLength);
    }
    if (maxLength !== null) {
      schema.maxLength(fieldName, maxLength);
    }
    return schema;
  },

  // URL validation
  url: (fieldName, required = true) => {
    const schema = createValidationSchema();
    if (required) {
      schema.required(fieldName, 'URL is required');
    }
    return schema.type(fieldName, 'url');
  },

  // Date validation
  date: (fieldName, required = true) => {
    const schema = createValidationSchema();
    if (required) {
      schema.required(fieldName, 'Date is required');
    }
    return schema.type(fieldName, 'date');
  },
};

export { ValidationSchema, createValidationSchema, validationSchemas };
